import { Add, MoreVertIcon } from '@tunasong/icons'
import { useAllowedChildEntityTypes, usePlugins } from '@tunasong/plugin-lib'
import { RoomMenu, RoomOverlay } from '@tunasong/plugin-video/ui'
import {
  Button,
  type ButtonProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  usePresence,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, ReactNode } from 'react'
import { useCallback, useState } from 'react'
import { useEntityMenuItems } from './entity-menu.hook.js'

import type { Entity, Persisted } from '@tunasong/schemas'
import { isFolderContainer } from '@tunasong/schemas'
import { CreateEntityDialog } from '../entity/create-entity-dialog.js'
import { ContactSupportMenuItem, KeyboardShortcutsDialog, KeyboardShortcutsMenuItem } from '../help/help-menu.js'
import styles from './entity-menu.module.css'

export interface EntityMenuButtonProps extends ButtonProps {
  className?: string
  entity?: Persisted<Entity>
  /** Header controls folded into the menu on small screens (e.g. Layout); listed first */
  leadingItems?: ReactNode
  /** Class for the More trigger button (`className` styles the menu popup) */
  triggerClassName?: string
  /** A dot on the trigger: something inside needs attention (say so in `aria-label` too) */
  unreadDot?: boolean
}

export const EntityMenuButton: FC<EntityMenuButtonProps> = props => {
  const {
    className,
    entity,
    leadingItems,
    triggerClassName,
    unreadDot,
    'aria-label': ariaLabel = 'Menu Dropdown Button',
    ...buttonProps
  } = props

  /**
   * Canonical Base UI composition: the menu is uncontrolled and closes itself on
   * item activation, the trigger lives in the tree, and submenu flyouts work. The
   * hook's `onClose`/`onCloseMenu` no longer toggle the menu — they only drive the
   * dialog lifecycle (the menu has already closed on item-press by then).
   */
  const noop = useCallback(() => undefined, [])

  /** @todo should we use presence here? */
  const present = usePresence(entity?.id)

  const { menuItems: entityMenuItems, dialogs: entityDialogs } = useEntityMenuItems({
    entity,
    onClose: noop,
    onCloseMenu: noop,
  })

  /** "New…": the header's create button lives here for everything except folders, which keep it in the bar */
  const plugins = usePlugins() ?? []
  const childTypes = useAllowedChildEntityTypes(entity, plugins)
  const showCreate = Boolean(entity && !isFolderContainer(entity) && childTypes.length > 0)
  const [createOpen, setCreateOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)

  /** Room overlay */
  const [showRoomOverlay, setShowRoomOverlay] = useState(false)
  const toggleRoom = useCallback(() => setShowRoomOverlay(o => !o), [])

  return (
    <>
      <Menu>
        <MenuTrigger
          render={
            <Button
              color="primary"
              aria-label={ariaLabel}
              className={cn(triggerClassName, unreadDot && styles.withDot)}
              {...buttonProps}
            >
              <MoreVertIcon />
              {unreadDot ? <span className={styles.unreadDot} data-unread-dot aria-hidden="true" /> : null}
            </Button>
          }
        />
        <MenuContent className={className}>
          {leadingItems}
          {showCreate ? (
            <MenuItem onClick={() => setCreateOpen(true)}>
              <ListItemIcon>
                <Add />
              </ListItemIcon>
              <ListItemText primary="New…" />
            </MenuItem>
          ) : null}

          {...entityMenuItems}

          <RoomMenu open={showRoomOverlay} entityId={entity?.id} onClick={toggleRoom} disabled={present.length === 0} />

          <KeyboardShortcutsMenuItem onShow={() => setShortcutsOpen(true)} />
          <ContactSupportMenuItem />
        </MenuContent>
      </Menu>

      {entity && showCreate ? (
        <CreateEntityDialog open={createOpen} onClose={() => setCreateOpen(false)} plugins={plugins} parent={entity} />
      ) : null}
      <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      {showRoomOverlay ? <RoomOverlay open={showRoomOverlay} entityId={entity?.id} /> : null}
      {...entityDialogs}
    </>
  )
}
